import { useEffect, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { useShoppingSession } from "@/lib/ShoppingSession";

/**
 * Canonical Terms & Conditions checkbox used across every purchase
 * surface (Store, Track page, Catalog list, Genre, Section, Cart drawer,
 * Bundle). All instances read/write the SAME shared shopping-session
 * state via `useShoppingSession`, so ticking the box on one surface is
 * instantly reflected on every other.
 *
 * Props:
 *   * variant   – "store" | "cart"  (visual density; cart is compact)
 *   * testId    – override for the checkbox `data-testid` when a page
 *                 wants a namespaced testid (Track page, Catalog, etc.)
 */
export default function TermsBlock({
  variant = "store",
  testId,
  labelClassName,
}) {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { termsAccepted, setTermsAccepted, nudged, registerTermsAnchor } = useShoppingSession();
  const anchorRef = useRef(null);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return undefined;
    return registerTermsAnchor(el);
  }, [registerTermsAnchor]);

  const compact = variant === "cart";
  const showNudge = nudged && !termsAccepted;

  const cbId = testId || "terms-checkbox";

  return (
    <div
      ref={anchorRef}
      data-testid={variant === "cart" ? "cart-terms-block" : "terms-block"}
      className={[
        "hairline rounded-xl bg-white/[0.02]",
        compact ? "p-3" : "p-4",
        showNudge ? "border-red-brand ring-1 ring-red-brand/40" : "",
      ].join(" ")}
    >
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          id={cbId}
          data-testid={cbId}
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="!w-4 !h-4 mt-1"
        />
        <span className={labelClassName || (compact ? "text-xs text-white/80 leading-relaxed" : "text-sm text-white/80")}>
          {t("terms.agreePrefix")}
          <a href={localisedTo("/terms", lang)} target="_blank" rel="noopener" className="text-red-brand underline">{t("terms.termsLink")}</a>
          {t("terms.and")}
          <a href={localisedTo("/refund", lang)} target="_blank" rel="noopener" className="text-red-brand underline">{t("terms.refundLink")}</a>
          {t("terms.period")}
        </span>
      </label>
      {showNudge && (
        <div
          role="alert"
          data-testid={`${variant === "cart" ? "cart-" : ""}terms-nudge`}
          className="mt-3 flex items-start gap-2 rounded-lg bg-red-brand/10 border border-red-brand/50 text-red-brand text-sm px-3 py-2"
        >
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>{t("terms.acceptFirst")}</span>
        </div>
      )}
    </div>
  );
}
